import { html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import { load } from "../../utils/media";

import styles from "./video-slide.css";

import type { VideoVisualData } from "../../types.d";
import type { SlideViewMode } from "../slide/slide";
import type { PropertyValueMap } from "lit";
import type { Ref } from "lit/directives/ref.js";

@customElement("bp-video-slide")
export class VideoSlide extends LitElement {
  static styles = styles;

  @property({ type: Number, attribute: "current-time" })
  currentTime: number = 0;

  @property()
  data?: VideoVisualData;

  @state()
  loadingImage = false;

  @property({
    type: Boolean,
    attribute: "no-video",
  })
  noVideo: boolean = false;

  @property({
    type: Boolean,
    attribute: "paused",
  })
  paused: boolean = false;

  @property({ type: Number, attribute: "playback-rate" })
  playbackRate = 1;

  @state()
  thumbnailTime?: number = undefined;

  videoRef: Ref<HTMLVideoElement> = createRef();

  @property({ attribute: "view-mode" })
  viewMode?: SlideViewMode = "default";

  render() {
    if (!this.data) return;

    const { src } = this.data;
    const playbackId =
      src.match(
        new RegExp("https://stream.mux.com/([a-zA-Z0-9]+).m3u8"),
      )?.[1] ?? "";

    if (!playbackId && this.noVideo) return;

    const thumbnailSrc = `https://image.mux.com/${
      playbackId ?? ""
    }/thumbnail.jpg?time=${this.thumbnailTime}&width=480&height=270`;

    return html`
      <div class="video-slide ${this.viewMode}">
        ${this.noVideo
          ? html`<img
              class="thumbnail"
              src=${thumbnailSrc}
              @load=${this.thumbnailLoaded}
            />`
          : html`<video
              muted
              playsinline
              @loadeddata=${this.updateVideo}
              ${ref(this.videoRef)}
            ></video>`}
      </div>
    `;
  }

  shouldUpdate(changedProperties: PropertyValueMap<this>) {
    let shouldUpdate =
      changedProperties.has("viewMode") ||
      changedProperties.has("data") ||
      changedProperties.has("noVideo");

    if (
      this.noVideo &&
      (changedProperties.has("currentTime") ||
        changedProperties.has("loadingImage")) &&
      !this.loadingImage &&
      this.thumbnailTime !== this.currentTime
    ) {
      this.loadingImage = true;
      this.thumbnailTime = this.currentTime ?? 0;
      shouldUpdate = true;
    } else if (
      changedProperties.has("currentTime") ||
      changedProperties.has("paused") ||
      changedProperties.has("playbackRate")
    ) {
      this.updateVideo();
    }

    return shouldUpdate;
  }

  thumbnailLoaded() {
    this.loadingImage = false;
  }

  protected firstUpdated(): void {
    if (!this.paused) {
      this.play();
    }
  }

  protected updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has("data") && this.videoRef.value) {
      load(this.videoRef.value, { src: this.data?.src });
    }
  }

  private async pause() {
    if (this.videoRef.value) {
      // Ensure the video is muted so it can be
      // programmatically played
      this.videoRef.value.muted = true;

      // Try to play
      this.videoRef.value
        .play()
        ?.then(() => {
          this.videoRef.value?.pause();
        })
        .catch(() => {
          console.error("Failed to pause video-slide video");
        });
    }
  }

  private async play() {
    if (this.videoRef.value) {
      // Ensure the video is muted so it can be
      // programmatically played
      this.videoRef.value.muted = true;

      // Try to play
      this.videoRef.value.play()?.catch(() => {
        console.error("Failed to play video-slide video");
      });
    }
  }

  private updateVideo() {
    if (!this.videoRef.value) return;

    if (this.playbackRate != this.videoRef.value.playbackRate) {
      this.videoRef.value.playbackRate = this.playbackRate;
    }

    if (this.videoRef.value.paused != this.paused) {
      if (this.paused) {
        this.pause();
      } else {
        this.play();
      }
    }

    if (
      this.currentTime &&
      (this.paused ||
        Math.abs(this.videoRef.value.currentTime - this.currentTime) > 1)
    ) {
      this.videoRef.value.currentTime = this.currentTime;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-video-slide": VideoSlide;
  }
}
